  // Os widgets (subscriptions/orders) desistem de esperar por kencko:bridge-ready ao fim
  // de 20s e mostram AUTH-TIMEOUT. Todo o trabalho de rede do bridge tem de caber nessa
  // janela: se não couber, o cliente vê um erro enquanto o bridge ainda está a tentar, e
  // o bridge-ready acaba por ser disparado quando já ninguém está à escuta.
  // Este é o orçamento total de rede do bridge (todas as tentativas somadas), com margem
  // para o resto do fluxo.
  const BRIDGE_NETWORK_BUDGET_MS = 17000;
  let bridgeBudgetStartedAt = Date.now();

  function resetBridgeBudget() {
  bridgeBudgetStartedAt = Date.now();
  }

  function bridgeBudgetLeft() {
  return BRIDGE_NETWORK_BUDGET_MS - (Date.now() - bridgeBudgetStartedAt);
  }

  // Retries transient network/timeout failures (common on slow connections or VPNs) before giving up.
  // Does not retry on responses that came back (4xx/5xx) — only on network errors / timeouts.
  // Cada tentativa é limitada pelo timeout E pelo orçamento acima — o que sobrar for menor.
  async function fetchWithRetry(
  url,
  options,
  { retries = 2, timeoutMs = 5000 } = {},
  ) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const budgetLeft = bridgeBudgetLeft();
    if (budgetLeft <= 0) {
    throw lastErr || new Error("Bridge network budget exhausted");
    }
    const controller = new AbortController();
    const timer = setTimeout(
    () => controller.abort(),
    Math.min(timeoutMs, budgetLeft),
    );
    try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timer);
    return res;
    } catch (err) {
    clearTimeout(timer);
    lastErr = err;
    const backoff = 1000 * (attempt + 1);
    if (attempt < retries && bridgeBudgetLeft() > backoff) {
      await new Promise((r) => setTimeout(r, backoff));
    } else {
      break;
    }
    }
  }
  throw lastErr;
  }

  // Notifies anything waiting on kencko:bridge-ready that the login flow failed,
  // so widgets never get stuck in an infinite loading state. `code` is a short,
  // user-facing identifier customers can quote to support.
  // Codes where retrying the same flow again is unlikely to help (config/state issues) —
  // widgets show a "contact support" message instead of "try again" for these.
  const BRIDGE_SUPPORT_ONLY_CODES = new Set([
  "AUTH-01",
  "AUTH-03",
  "AUTH-04",
  "AUTH-08",
  ]);

  function dispatchBridgeFailure(code, reason) {
  document.dispatchEvent(
    new CustomEvent("kencko:bridge-ready", {
    detail: {
      accessToken: null,
      error: true,
      code,
      reason,
      supportOnly: BRIDGE_SUPPORT_ONLY_CODES.has(code),
    },
    }),
  );
  }

  // ─── CREDENCIAIS GUARDADAS (fallback de login) ────────────
  // Quando o OAuth silencioso da Shopify falha — típico em VPNs que passam pela
  // Cloudflare e bloqueiam/atrasam os domínios da Shopify — o tema recupera a sessão
  // refazendo o login diretamente no backend kencko com o email + password do cliente.
  // Para isso a password é guardada em localStorage, CIFRADA (AES-GCM) e sob uma chave
  // ofuscada — nunca em claro. É obfuscação, não segredo forte: a chave de cifra vive no
  // próprio JS, por isso um XSS conseguiria decifrá-la. Ver docs/auth-and-account-data-flow.md.

  // Nome ofuscado da entrada em localStorage (a "key" cifrada) — não revela o conteúdo.
  const CRED_STORE_KEY = "k_x9f2c";
  // Passphrase fixa usada para derivar a chave AES. Só ofusca (não é um segredo real).
  const CRED_SECRET = "kencko::cred::v1::a7d93f2b";

  async function deriveCredKey() {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(CRED_SECRET),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
    name: "PBKDF2",
    salt: enc.encode("kencko-cred-salt-v1"),
    iterations: 100000,
    hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
  }

  async function encryptCred(obj) {
  const key = await deriveCredKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(obj));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
  const combined = new Uint8Array(iv.length + ct.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ct), iv.length);
  let binary = "";
  combined.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
  }

  async function decryptCred(b64) {
  const key = await deriveCredKey();
  const combined = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const iv = combined.slice(0, 12);
  const ct = combined.slice(12);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(pt));
  }

  async function storeCredentials(email, password) {
  if (!email || !password) return;
  try {
    const encrypted = await encryptCred({ e: email, p: password });
    localStorage.setItem(CRED_STORE_KEY, encrypted);
  } catch (err) {
    console.warn(
    "[LoginBridge] Não foi possível guardar credenciais cifradas:",
    err.message,
    );
  }
  }

  async function getStoredCredentials() {
  const raw = localStorage.getItem(CRED_STORE_KEY);
  if (!raw) return null;
  try {
    return await decryptCred(raw);
  } catch (err) {
    console.warn(
    "[LoginBridge] Falha ao decifrar credenciais guardadas:",
    err.message,
    );
    return null;
  }
  }

  function hasStoredCredentials() {
  return !!localStorage.getItem(CRED_STORE_KEY);
  }

  function clearStoredCredentials() {
  localStorage.removeItem(CRED_STORE_KEY);
  }

  // POST direto ao backend kencko com email+password. Devolve o token (Bearer) se o
  // backend o incluir no corpo, ou null caso contrário / em erro (não lança).
  async function backendPasswordLogin(nextLoginUrl, email, password) {
  const body = new URLSearchParams({ email, password });
  const res = await fetchWithRetry(
    nextLoginUrl,
    {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      shopifystore: window.Shopify?.shop,
    },
    body: body.toString(),
    },
    // Um único pedido por chamada — as 2 tentativas são o loop externo em
    // attemptCredentialRelogin, para não exceder o timeout de 20s dos widgets.
    { retries: 0, timeoutMs: 8000 },
  );
  if (!res.ok) return null;
  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = JSON.parse(text);
  } catch (_) {}
  return data?.token || data?.access_token || data?.data?.token || null;
  }

  // Logout forçado: limpa toda a sessão + credenciais e termina também a sessão Shopify
  // via /account/logout (senão a página de login, com `{% if customer %}`, faria bounce
  // de volta para /account e entrava em loop). O marcador `kencko-relogin-expired` fica
  // em sessionStorage para o formulário de login mostrar que é preciso reautenticar,
  // independentemente da página onde o /account/logout aterre.
  function forceReloginRedirect() {
  clearLoginBridgeStorage();
  clearStoredCredentials();
  try {
    sessionStorage.setItem("kencko-relogin-expired", "1");
  } catch (e) {}
  window.location.replace("/account/logout");
  }

  class LoginBridge {
  constructor() {
    if (window.Shopify && window.Shopify.designMode) return;

    const accountConfig = document.getElementById("login-bridge-config");
    if (!accountConfig) return;

    this.nextLoginUrl = accountConfig.dataset.nextLoginUrl || "";
    this.customerEmail = accountConfig.dataset.customerEmail || "";
    this.clientId = accountConfig.dataset.clientId || "";
    this.shopDomain = accountConfig.dataset.shopDomain || "";
    this.redirectUri = window.location.origin + "/account";

    if (!this.clientId) return;

    // O relógio de 20s dos widgets começa aqui (também eles arrancam no DOMContentLoaded),
    // por isso o orçamento de rede do bridge conta a partir deste momento.
    resetBridgeBudget();

    this.run();
  }

  run() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");

    // Case 1: OAuth returned an error
    if (error) {
    if (error === "login_required") {
      // Silent flow failed — the browser has no active session with the Customer Account
      // API (common for customers who only ever logged in via the classic email/password
      // form, which never establishes that session). This store uses classic accounts, so
      // there's no interactive fallback here — escalating to the interactive OAuth screen
      // would send the customer to Shopify's hosted (non-branded) login page, which isn't
      // the store's real login system. Fail straight to the widgets' password fallback instead.
      localStorage.setItem("kencko-bridge-done", "true");
      sessionStorage.removeItem("kencko-bridge-started");
      sessionStorage.removeItem("kencko-bridge-fallback");
      sessionStorage.removeItem("kencko-interactive-attempted");
      this.cleanUrl();
      dispatchBridgeFailure(
      "AUTH-02",
      "No active Shopify Customer Account API session (login_required)",
      );
      return;
    }
    localStorage.setItem("kencko-bridge-done", "true");
    sessionStorage.removeItem("kencko-bridge-fallback");
    this.cleanUrl();
    dispatchBridgeFailure(
      "AUTH-01",
      `OAuth provider returned error: ${error}${params.get("error_description") ? " - " + params.get("error_description") : ""}`,
    );
    return;
    }

    // Case 2: OAuth returned a code — exchange it for a token
    if (code) {
    this.handleOAuthCallback(code, state);
    return;
    }

    // Fast path (recuperação por credenciais): chegámos aqui sem ?code nem ?error.
    // Se não há token utilizável mas temos credenciais guardadas, refazemos o login
    // diretamente no backend kencko em vez de passar pelo OAuth silencioso da Shopify
    // (que é o que falha com VPNs/Cloudflare). Isto evita o redirect problemático por
    // completo para quem entrou pelo formulário clássico. Sem credenciais, segue o
    // fluxo OAuth original abaixo (Case 3/4/5), mantendo a compatibilidade.
    {
    const storedToken = localStorage.getItem("kencko-access-token");
    const authRetry = localStorage.getItem("kencko-auth-retry") === "true";
    const hasUsableToken = storedToken && !authRetry;
    if (!hasUsableToken && this.nextLoginUrl && hasStoredCredentials()) {
      this.recoverWithCredentials();
      return;
    }
    }

    // Case 3: Already completed the bridge this session (via PKCE or storefront login)
    if (localStorage.getItem("kencko-bridge-done") === "true") {
    const storedToken = localStorage.getItem("kencko-access-token");
    const authRetry = localStorage.getItem("kencko-auth-retry") === "true";
    if (storedToken && !authRetry) {
      console.debug(
      "[LoginBridge] Case 3: Token encontrado, disparando bridge-ready",
      );
      // Notify any listeners (e.g. account page loaded without ?code) that a token is available.
      document.dispatchEvent(
      new CustomEvent("kencko:bridge-ready", {
        detail: { accessToken: storedToken },
      }),
      );
    } else if (!storedToken && !authRetry) {
      // Estado inválido: marcado como done mas sem token — tentar OAuth de novo
      console.warn(
      "[LoginBridge] Case 3: Estado inválido (done mas sem token), tentando OAuth silencioso",
      );
      sessionStorage.setItem("kencko-oauth-retry", "true");
      localStorage.removeItem("kencko-bridge-done");
      this.startOAuthFlow(true);
    } else if (!sessionStorage.getItem("kencko-oauth-retry")) {
      // Token missing or a 401 retry was requested — redo OAuth once per session to recover.
      console.debug(
      "[LoginBridge] Case 3: Retry de OAuth solicitado, tentando novamente",
      );
      sessionStorage.setItem("kencko-oauth-retry", "true");
      localStorage.removeItem("kencko-bridge-done");
      this.startOAuthFlow(true);
    } else {
      // Already retried once this session and still no usable token — stop looping.
      sessionStorage.removeItem("kencko-oauth-retry");
      dispatchBridgeFailure(
      "AUTH-09",
      "Retried OAuth once this session and still have no usable token",
      );
    }
    return;
    }

    // Case 4: Already started redirect but came back without code/error (safety valve)
    if (sessionStorage.getItem("kencko-bridge-started") === "true") {
    localStorage.setItem("kencko-bridge-done", "true");
    sessionStorage.removeItem("kencko-bridge-started");
    this.cleanUrl();
    dispatchBridgeFailure('AUTH-10', 'Returned from OAuth redirect without a code or error param (interrupted flow)');
    return;
    }

    // Case 5: Fresh session — start silent OAuth flow
    this.startOAuthFlow(true);
  }

  // ─── RECUPERAÇÃO POR CREDENCIAIS ──────────────────────────

  // Tenta recuperar a sessão via backend (email+password guardados). Se não houver
  // credenciais decifráveis, cai para o OAuth original. O attempt em si trata do
  // sucesso (dispara bridge-ready) e da falha definitiva (logout + redirect).
  async recoverWithCredentials() {
    const usedCredentials = await this.attemptCredentialRelogin(2);
    if (!usedCredentials) {
    // Sem credenciais utilizáveis (ex. falha ao decifrar) — segue o OAuth normal.
    this.startOAuthFlow(true);
    }
  }

  // Refaz o login no backend com o email+password guardados, até maxAttempts vezes.
  // - Sucesso: guarda o token, dispara kencko:bridge-ready e devolve true.
  // - Credenciais válidas mas login falhou em todas as tentativas: força logout e
  //   reencaminha para o login (devolve true — já está a redirecionar).
  // - Sem credenciais utilizáveis: devolve false (o chamador faz fallback ao OAuth).
  async attemptCredentialRelogin(maxAttempts) {
    const creds = await getStoredCredentials();
    const email = (creds && creds.e) || this.customerEmail || "";
    const password = creds && creds.p;
    if (!email || !password) return false;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    console.debug(
      `[LoginBridge] Relogin por credenciais, tentativa ${attempt}/${maxAttempts}`,
    );
    let token = null;
    try {
      token = await backendPasswordLogin(this.nextLoginUrl, email, password);
    } catch (err) {
      console.warn(
      "[LoginBridge] Relogin por credenciais falhou (rede):",
      err.message,
      );
    }
    if (token) {
      console.debug("[LoginBridge] Relogin por credenciais bem-sucedido");
      localStorage.setItem("kencko-access-token", token);
      localStorage.setItem("kencko-bridge-done", "true");
      localStorage.removeItem("kencko-auth-retry");
      sessionStorage.removeItem("kencko-oauth-retry");
      sessionStorage.removeItem("kencko-reauth-401");
      this.cleanUrl();
      document.dispatchEvent(
      new CustomEvent("kencko:bridge-ready", {
        detail: { accessToken: token },
      }),
      );
      return true;
    }
    }

    // Esgotou as tentativas com credenciais válidas — força novo login.
    console.warn(
      "[LoginBridge] Relogin por credenciais esgotou as tentativas — a forçar logout",
    );
    forceReloginRedirect();
    return true;
  }

  // ─── OAUTH FLOW ───────────────────────────────────────────

  async startOAuthFlow(silent = true) {
    try {
    console.debug("[LoginBridge] Iniciando OAuth flow (silent=" + silent + ")");
    sessionStorage.setItem("kencko-bridge-started", "true");

    const oidcConfig = await this.discoverEndpoints();
    console.debug("[LoginBridge] Endpoints descobertos");

    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    localStorage.setItem("kencko-code-verifier", codeVerifier);

    const state =
      Date.now().toString(36) + Math.random().toString(36).substring(2);
    sessionStorage.setItem("kencko-oauth-state", state);

    const authUrl = new URL(oidcConfig.authorization_endpoint);
    authUrl.searchParams.set("scope", "openid email customer-account-api:full");
    authUrl.searchParams.set("client_id", this.clientId);
    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("redirect_uri", this.redirectUri);
    authUrl.searchParams.set("state", state);
    authUrl.searchParams.set("nonce", this.generateNonce(32));
    authUrl.searchParams.set("code_challenge", codeChallenge);
    authUrl.searchParams.set("code_challenge_method", "S256");
    if (silent) {
      authUrl.searchParams.set("prompt", "none");
    }

    window.location.href = authUrl.toString();
    } catch (err) {
    console.error("[LoginBridge] Falha ao iniciar OAuth flow:", err.message);
    localStorage.setItem("kencko-bridge-done", "true");
    sessionStorage.removeItem("kencko-bridge-started");
    sessionStorage.removeItem("kencko-bridge-fallback");
    sessionStorage.removeItem("kencko-oauth-state");
    dispatchBridgeFailure(
      "AUTH-05",
      `Failed to start OAuth flow: ${err.message}`,
    );
    }
  }

  async handleOAuthCallback(code, state) {
    try {
    console.debug("[LoginBridge] handleOAuthCallback chamado com code");
    const savedState = sessionStorage.getItem("kencko-oauth-state");
    sessionStorage.removeItem("kencko-oauth-state");
    sessionStorage.removeItem("kencko-bridge-started");

    if (state && savedState && state !== savedState) {
      localStorage.setItem("kencko-bridge-done", "true");
      this.cleanUrl();
      dispatchBridgeFailure(
      "AUTH-03",
      "OAuth state mismatch (possible CSRF or stale/duplicate callback)",
      );
      return;
    }

    const oidcConfig = await this.discoverEndpoints();

    const codeVerifier = localStorage.getItem("kencko-code-verifier");
    localStorage.removeItem("kencko-code-verifier");

    if (!codeVerifier) {
      localStorage.setItem("kencko-bridge-done", "true");
      sessionStorage.removeItem("kencko-bridge-fallback");
      this.cleanUrl();
      dispatchBridgeFailure(
      "AUTH-04",
      "Missing PKCE code_verifier (localStorage cleared/blocked mid-flow)",
      );
      return;
    }

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      code: code,
      code_verifier: codeVerifier,
    });

    const tokenResponse = await fetchWithRetry(oidcConfig.token_endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });

    if (!tokenResponse.ok) {
      console.error(
      "[LoginBridge] Token endpoint retornou status",
      tokenResponse.status,
      );
      localStorage.setItem("kencko-bridge-done", "true");
      sessionStorage.removeItem("kencko-bridge-fallback");
      this.cleanUrl();
      dispatchBridgeFailure(
      "AUTH-06",
      `Token endpoint returned HTTP ${tokenResponse.status}`,
      );
      return;
    }

    const tokenData = await tokenResponse.json();

    localStorage.setItem("kencko-bridge-done", "true");
    console.debug("[LoginBridge] Bridge marcado como done");
    sessionStorage.removeItem("kencko-bridge-fallback");

    if (tokenData.access_token) {
      console.debug("[LoginBridge] Token recebido de Shopify");
      localStorage.setItem("kencko-access-token", tokenData.access_token);
      localStorage.removeItem("kencko-auth-retry");
      sessionStorage.removeItem("kencko-oauth-retry");
      sessionStorage.removeItem("kencko-interactive-attempted");
      this.cleanUrl();
      console.debug("[LoginBridge] Disparando bridge-ready com token");
      document.dispatchEvent(
      new CustomEvent("kencko:bridge-ready", {
        detail: { accessToken: tokenData.access_token },
      }),
      );

      // Best effort, NÃO bloqueante: notifica o backend para ele definir o seu
      // próprio cookie de sessão. Os widgets autenticam-se com o Bearer token
      // (não com esse cookie), por isso NÃO se faz await — um backend lento/
      // inacessível aqui prendia todo o bridge para lá dos 20s de espera dos
      // widgets e aparecia como AUTH-TIMEOUT, mesmo com o token OAuth já obtido.
      if (this.nextLoginUrl) {
      console.debug("[LoginBridge] Chamando backend login com token (background)");
      this.loginNextBackend(tokenData.access_token);
      }
      return;
    }

    // Token endpoint responded 2xx but without an access_token — unexpected shape.
    dispatchBridgeFailure(
      "AUTH-08",
      "Token endpoint responded OK but returned no access_token",
    );
    } catch (err) {
    console.error("[LoginBridge] handleOAuthCallback erro:", err.message);
    localStorage.setItem("kencko-bridge-done", "true");
    sessionStorage.removeItem("kencko-bridge-fallback");
    sessionStorage.removeItem("kencko-oauth-state");
    dispatchBridgeFailure(
      "AUTH-07",
      err.message || "Unknown error during token exchange",
    );
    }

    this.cleanUrl();
  }

  // ─── BACKEND CALL ─────────────────────────────────────────

  async loginNextBackend(accessToken) {
    // Own timeout so a hung connection to the backend doesn't linger — this runs
    // in the background (not awaited), but we still abort it after 8s.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
    const response = await fetch(this.nextLoginUrl, {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
      headers: {
      Authorization: `Bearer ${accessToken}`,
      shopifystore: window.Shopify?.shop,
      },
    });
    if (!response.ok) {
      console.warn(
      "[LoginBridge] Backend login retornou status",
      response.status,
      );
    } else {
      console.debug("[LoginBridge] Backend login bem-sucedido");
    }
    } catch (err) {
    console.error("[LoginBridge] Erro ao contactar backend login:", err.message);
    } finally {
    clearTimeout(timer);
    }
  }

  // ─── OIDC DISCOVERY ───────────────────────────────────────

  // O documento de discovery é estático por loja. O fluxo passa por ele duas vezes —
  // uma ao arrancar o OAuth, outra no callback, já noutro carregamento de página — por
  // isso fica em sessionStorage: poupa um round-trip no caminho crítico do callback,
  // que é precisamente onde os widgets estão à espera do token. Só se guarda o que
  // interessa, e um documento inválido em cache é descartado.
  async discoverEndpoints() {
    const cacheKey = `kencko-oidc-config:${this.shopDomain}`;
    try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || "null");
    if (cached && cached.authorization_endpoint && cached.token_endpoint) {
      console.debug("[LoginBridge] Discovery em cache");
      return cached;
    }
    } catch (e) {
    /* cache ilegível — segue para o pedido */
    }

    const url = `https://${this.shopDomain}/.well-known/openid-configuration`;
    try {
    const response = await fetchWithRetry(url);
    if (!response.ok) {
      console.error("[LoginBridge] Discovery falhou com status", response.status);
      throw new Error(`Discovery failed: ${response.status}`);
    }
    console.debug("[LoginBridge] Discovery bem-sucedido");
    const config = await response.json();
    if (config?.authorization_endpoint && config?.token_endpoint) {
      try {
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
        authorization_endpoint: config.authorization_endpoint,
        token_endpoint: config.token_endpoint,
        }),
      );
      } catch (e) {}
    }
    return config;
    } catch (err) {
    console.error("[LoginBridge] Erro ao descobrir endpoints:", err.message);
    throw err;
    }
  }

  // ─── PKCE HELPERS ─────────────────────────────────────────

  generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return this.base64UrlEncode(
    String.fromCharCode.apply(null, Array.from(array)),
    );
  }

  async generateCodeChallenge(codeVerifier) {
    const digest = await crypto.subtle.digest(
    { name: "SHA-256" },
    new TextEncoder().encode(codeVerifier),
    );
    const hash = String.fromCharCode.apply(
    null,
    Array.from(new Uint8Array(digest)),
    );
    return this.base64UrlEncode(hash);
  }

  base64UrlEncode(str) {
    return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
  }

  generateNonce(length) {
    const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let nonce = "";
    for (let i = 0; i < length; i++) {
    nonce += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return nonce;
  }

  // ─── UTILS ────────────────────────────────────────────────

  cleanUrl() {
    const url = new URL(window.location.href);
    const dirtyParams = [
    "code",
    "state",
    "error",
    "error_description",
    "client_id",
    ];
    let changed = false;

    dirtyParams.forEach((p) => {
    if (url.searchParams.has(p)) {
      url.searchParams.delete(p);
      changed = true;
    }
    });

    if (changed) {
    window.history.replaceState({}, "", url.pathname + (url.search || ""));
    }
  }
  }

  class LoginBridgeStorefront {
  constructor(form, nextLoginUrl) {
    this.form = form;
    this.nextLoginUrl = nextLoginUrl;

    if (!this.nextLoginUrl) return;

    this.form.addEventListener("submit", (e) => this.handleSubmit(e));
  }

  setLoading(loading) {
    const btn = this.form.querySelector('[type="submit"]');
    if (!btn) return;
    if (loading) {
    btn.classList.add("btn--loading");
    btn.disabled = true;
    } else {
    btn.classList.remove("btn--loading");
    btn.disabled = false;
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    this.setLoading(true);

    const email = this.form
    .querySelector('[name="customer[email]"]')
    ?.value?.trim();
    const password = this.form.querySelector(
    '[name="customer[password]"]',
    )?.value;

    if (email && password) {
    // Guarda as credenciais cifradas para o fallback de relogin no /account quando o
    // OAuth silencioso da Shopify falhar (VPN/Cloudflare). Ver login-bridge fast path.
    await storeCredentials(email, password);
    // Timeout próprio: sem isto, um backend lento (não necessariamente em baixo) deixa
    // esta promise pendurada para sempre, o form.submit() de fallback nunca corre, e o
    // botão fica preso em loading/disabled — o oposto do "nunca bloqueia o cliente" que
    // este método garante em todos os outros ramos.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      console.debug("[LoginBridgeStorefront] Iniciando login com email:", email);
      const body = new URLSearchParams({ email, password });
      const response = await fetch(this.nextLoginUrl, {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        shopifystore: window.Shopify?.shop,
      },
      body: body.toString(),
      });

      if (response.ok) {
      // Try response body for token (if backend returns it)
      const text = await response.text().catch(() => "");
      let data = {};
      try {
        data = JSON.parse(text);
      } catch (_) {}
      const token = data?.token || data?.access_token || data?.data?.token;

      if (token) {
        console.debug(
        "[LoginBridgeStorefront] Token recebido do backend, armazenando",
        );
        localStorage.setItem("kencko-access-token", token);
      } else {
        console.debug(
        "[LoginBridgeStorefront] Backend não retornou token (cookie apenas)",
        );
      }
      // Proceed regardless — backend already set the auth cookie via Set-Cookie
      localStorage.setItem("kencko-bridge-done", "true");
      console.debug(
        "[LoginBridgeStorefront] Marcando bridge como done, redirecionando",
      );
      this.form.submit();
      return;
      }

      // Backend returned a non-2xx — fall back to Shopify login
      console.warn(
      "[LoginBridgeStorefront] Backend retornou status",
      response.status,
      "— voltando ao login Shopify",
      );
      localStorage.setItem("kencko-bridge-done", "true");
      this.form.submit();
      return;
    } catch (err) {
      // Network error, CORS, or our own 8s abort — fall back to Shopify login
      console.error("[LoginBridgeStorefront] Erro no login:", err.message);
      localStorage.setItem("kencko-bridge-done", "true");
      this.form.submit();
      return;
    } finally {
      clearTimeout(timer);
    }
    }

    // No email/password present — let Shopify handle its own validation
    console.debug(
    "[LoginBridgeStorefront] Email ou password vazios, deixando Shopify validar",
    );
    this.form.submit();
  }
  }

  // Exposed so widgets' retry buttons can restart the OAuth flow when no token is available yet.
  window.kenckoRetryLogin = function () {
  sessionStorage.removeItem("kencko-oauth-retry");
  sessionStorage.removeItem("kencko-bridge-started");
  sessionStorage.removeItem("kencko-interactive-attempted");
  localStorage.removeItem("kencko-bridge-done");
  localStorage.removeItem("kencko-auth-retry");
  // Se temos credenciais guardadas, descarta qualquer token velho/inválido para que o
  // LoginBridge tome o fast path de relogin por credenciais (em vez do OAuth frágil).
  if (hasStoredCredentials()) localStorage.removeItem("kencko-access-token");
  new LoginBridge();
  };

  function clearLoginBridgeStorage() {
  localStorage.removeItem("kencko-bridge-done");
  localStorage.removeItem("kencko-access-token");
  localStorage.removeItem("kencko-code-verifier");
  localStorage.removeItem("kencko-auth-retry");
  sessionStorage.removeItem("kencko-bridge-started");
  sessionStorage.removeItem("kencko-oauth-state");
  sessionStorage.removeItem("kencko-bridge-fallback");
  sessionStorage.removeItem("kencko-oauth-retry");
  sessionStorage.removeItem("kencko-interactive-attempted");
  }

  document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll('a[href="/account/logout"]').forEach((link) => {
    link.addEventListener("click", () => {
    clearLoginBridgeStorage();
    clearStoredCredentials();
    });
  });

  const config = document.getElementById("login-bridge-config");
  const loginForm = document.querySelector(
    'form[action*="customer/login"], form[action*="account/login"]',
  );

  if (loginForm) {
    loginForm.addEventListener("submit", clearLoginBridgeStorage);
    if (config) {
    new LoginBridgeStorefront(loginForm, config.dataset.nextLoginUrl || "");
    }
  } else {
    new LoginBridge();
  }
  });
